import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { getDaysInCurrentStatus } from '../utils/leadStatusAge';

function normalize(s) {
  return (s || '').toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss');
}

function leadMatches(lead, q, statusMeta) {
  if (!q) return true;
  const nq = normalize(q);
  const statusLabel = (statusMeta[lead.status]?.label || lead.status || '').toLowerCase();
  return (
    normalize(lead.name).includes(nq) ||
    normalize(lead.company).includes(nq) ||
    (lead.email || '').toLowerCase().includes(q.trim().toLowerCase()) ||
    statusLabel.includes(q.trim().toLowerCase())
  );
}

export default function CommandPalette({
  isOpen,
  onClose,
  leads = [],
  statusList = [],
  statusMeta = {},
  selectedLead,
  activeSection,
  onOpenLead,
  onUpdateLead,
  onNewContact,
  onStartActivity,
  onGoDashboard,
  onGoContacts,
}) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setActive(0);
    const t = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(t);
  }, [isOpen]);

  const items = useMemo(() => {
    const q = query.trim();
    const out = [];

    const add = (row) => {
      out.push({ ...row, _key: row.key || row.title + (row.subtitle || '') });
    };

    const qn = normalize(q);
    const navMatch = !q || normalize('Dashboard').includes(qn) || qn.includes('dash');
    const contactsMatch = !q || normalize('Kontakte').includes(qn) || qn.includes('lead') || qn.includes('kont');

    if (navMatch && activeSection !== 'dashboard') {
      add({ key: 'nav-dash', kind: 'action', title: 'Dashboard öffnen', subtitle: 'Übersicht', run: onGoDashboard });
    }
    if (contactsMatch && activeSection !== 'contacts' && activeSection !== 'leads') {
      add({ key: 'nav-contacts', kind: 'action', title: 'Kontakte / Leads', subtitle: 'Zur Lead-Liste', run: onGoContacts });
    }

    if (!q || 'neuer lead'.includes(qn) || qn.includes('neu') || qn.includes('lead')) {
      add({ key: 'new', kind: 'action', title: 'Neuer Lead', subtitle: 'Kontakt anlegen (N)', run: onNewContact });
    }

    if (selectedLead) {
      const stLabel = statusMeta[selectedLead.status]?.label || selectedLead.status;
      if (!q || qn.includes('aufg') || qn.includes('aktiv') || qn.includes('task')) {
        add({
          key: 'task-sel',
          kind: 'action',
          title: 'Neue Aufgabe / Aktivität',
          subtitle: `${selectedLead.name} · ${stLabel}`,
          run: () => onStartActivity(selectedLead),
        });
      }
      statusList.forEach((s) => {
        if (s.id === selectedLead.status) return;
        const label = s.label || s.id;
        if (q && !normalize(label).includes(qn) && !qn.includes('status') && !qn.includes('stati')) return;
        add({
          key: `st-${selectedLead.id}-${s.id}`,
          kind: 'action',
          title: `Status: ${label}`,
          subtitle: `für ${selectedLead.name}`,
          run: () => onUpdateLead(selectedLead.id, { status: s.id }),
        });
      });
    }

    const matchedLeads = leads
      .filter((l) => leadMatches(l, q, statusMeta))
      .slice(0, 12)
      .map((l) => {
        const days = getDaysInCurrentStatus(l);
        const age = days != null ? ` · ${days} T. im Status` : '';
        return {
          key: `lead-${l.id}`,
          kind: 'lead',
          title: l.name,
          subtitle: `${l.company || '–'}${age}`,
          run: () => onOpenLead(l),
        };
      });
    matchedLeads.forEach(add);

    return out;
  }, [query, leads, statusList, statusMeta, selectedLead, activeSection, onGoDashboard, onGoContacts, onNewContact, onStartActivity, onUpdateLead, onOpenLead]);

  useEffect(() => {
    setActive((i) => Math.min(i, Math.max(0, items.length - 1)));
  }, [items.length]);

  const run = useCallback(
    (index) => {
      const row = items[index];
      if (!row?.run) return;
      row.run();
      onClose();
    },
    [items, onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => Math.min(items.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        run(active);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, items.length, active, run]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Befehlspalette"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 8000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '12vh',
        background: 'rgba(15,23,42,0.55)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 'min(540px, calc(100vw - 24px))',
          borderRadius: 20,
          overflow: 'hidden',
          position: 'relative',
          background: 'var(--glass-modal)',
          backdropFilter: 'var(--glass-blur-lg)',
          WebkitBackdropFilter: 'var(--glass-blur-lg)',
          border: '1px solid rgba(255,255,255,0.84)',
          boxShadow: '0 40px 90px rgba(15,40,100,0.28), 0 12px 36px rgba(15,40,100,0.16), 0 0 0 0.5px rgba(255,255,255,0.9), inset 0 1px 0 rgba(255,255,255,1)',
          animation: 'glModalIn .28s var(--motion-ease) both',
        }}
      >
        {/* Prism shimmer line */}
        <div style={{ position: 'absolute', top: 0, left: '8%', right: '8%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(180,220,255,0.9) 30%, rgba(255,255,255,1) 50%, rgba(220,190,255,0.9) 70%, transparent)', borderRadius: 999, pointerEvents: 'none', zIndex: 1 }} />

        <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid rgba(255,255,255,0.45)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/></svg>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', letterSpacing: '0.09em', textTransform: 'uppercase' }}>
              Befehlspalette
            </div>
            <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text3)', background: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: 5, padding: '1px 6px', fontFamily: 'var(--mono)' }}>ESC</span>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            placeholder="Lead suchen, Aktion wählen…"
            className="gl-search"
            style={{
              width: '100%',
              height: 42,
              borderRadius: 12,
              padding: '0 14px',
              fontSize: 15,
              outline: 'none',
              color: 'var(--text)',
            }}
          />
        </div>
        <div style={{ maxHeight: 'min(52vh, 420px)', overflowY: 'auto' }}>
          {items.length === 0 ? (
            <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Keine Treffer</div>
          ) : (
            items.map((row, i) => {
              const sel = i === active;
              return (
                <button
                  key={row._key}
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => run(i)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 14px',
                    border: 'none',
                    borderBottom: '1px solid var(--border)',
                    background: sel ? 'rgba(29,190,116,0.12)' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{row.title}</span>
                  {row.subtitle && <span style={{ fontSize: 12, color: 'var(--text3)' }}>{row.subtitle}</span>}
                </button>
              );
            })
          )}
        </div>
        <div
          style={{
            padding: '8px 14px',
            fontSize: 11,
            color: 'var(--text3)',
            borderTop: '1px solid rgba(255,255,255,0.40)',
            background: 'rgba(255,255,255,0.22)',
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <span>↑↓ Navigation · Enter ausführen · Esc schließen</span>
            <span style={{ fontFamily: 'var(--mono)' }}>⌘K · /</span>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 4, borderTop: '1px solid var(--border)' }}>
            {[['T', 'Neue Aufgabe'], ['N', 'Neuer Lead'], ['⌘⇧M', 'Neue Notiz'], ['⌘K', 'Palette']].map(([k, label]) => (
              <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <kbd style={{ fontFamily: 'var(--mono)', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px', fontSize: 10 }}>{k}</kbd>
                <span>{label}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
